import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDuration } from '../lib/format.js';

// `now` is passed in from TimeTracker, which owns the single 1Hz tick.
// Previously this component ran its own setInterval, which drifted from
// the parent's clock and produced the user-visible PiP-vs-dashboard
// timer discrepancy. One source of truth eliminates the drift.
export default function PipTimer({ runningLog, runningItem, now, onComplete, onPause, onNext, onClose, onReturnToTab }) {
  const [pipWindow, setPipWindow] = useState(null);
  const opening = useRef(false);

  useEffect(() => {
    if (!('documentPictureInPicture' in window) || pipWindow || opening.current) return;
    opening.current = true;

    (async () => {
      try {
        const w = await window.documentPictureInPicture.requestWindow({
          width: 320,
          height: 200
        });

        for (const styleSheet of Array.from(document.styleSheets)) {
          try {
            const cssRules = Array.from(styleSheet.cssRules).map(r => r.cssText).join('\n');
            const style = w.document.createElement('style');
            style.textContent = cssRules;
            w.document.head.appendChild(style);
          } catch {
            const link = w.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = styleSheet.href;
            w.document.head.appendChild(link);
          }
        }

        const themeAttr = document.documentElement.getAttribute('data-theme');
        if (themeAttr) w.document.documentElement.setAttribute('data-theme', themeAttr);

        w.document.body.style.margin = '0';
        w.document.body.style.background = 'var(--bg)';
        w.document.body.style.color = 'var(--text)';
        w.document.body.style.fontFamily = "'Inter', system-ui, sans-serif";
        w.document.title = 'Timer';

        w.addEventListener('pagehide', () => {
          setPipWindow(null);
          onClose?.();
        });

        setPipWindow(w);
      } catch (err) {
        console.error('Failed to open PiP:', err);
        onClose?.();
      } finally {
        opening.current = false;
      }
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (pipWindow && !pipWindow.closed) pipWindow.close();
    };
  }, [pipWindow]);

  if (!pipWindow) return null;

  // `now` comes from the parent so elapsed always matches the dashboard
  // timer to the second. If parent hasn't passed `now` yet, fall back to
  // Date.now() — better a slight drift than NaN.
  const tickNow = typeof now === 'number' ? now : Date.now();
  const elapsed = runningLog ? Math.floor((tickNow - new Date(runningLog.startTime).getTime()) / 1000) : 0;
  const longRunning = elapsed > 7200;
  const hasNext = Boolean(runningItem?.nextSubTask?.id);

  const handleReturn = () => {
    // Tell the parent tab to surface the running task. The parent's
    // focusRunningTask also closes the PiP, but we belt-and-brace by
    // also focusing the parent window first (some browsers won't
    // refocus on PiP close alone).
    try { window.opener?.focus?.(); } catch {}
    try { window.focus(); } catch {}
    onReturnToTab?.();
  };

  const content = (
    <div className="min-h-screen bg-bg text-text p-3 flex flex-col">
      <div className="flex items-center justify-between mb-0.5">
        <div className="text-[10px] uppercase tracking-wider text-muted">
          {runningLog ? 'Active' : 'Idle'}
        </div>
        {onReturnToTab && (
          <button
            onClick={handleReturn}
            title="Open Co-Pilot with this task front-and-center"
            className="text-[10px] text-muted hover:text-accent inline-flex items-center gap-1"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 6l-6 6 6 6M3 12h18" />
            </svg>
            Back to Co-Pilot
          </button>
        )}
      </div>
      <div className="text-xs font-medium truncate" title={runningLog?.subTaskName}>
        {runningLog?.subTaskName || 'No active timer'}
      </div>
      {runningLog?.taskName && (
        <div className="text-[10px] text-muted truncate">in {runningLog.taskName}</div>
      )}

      <div className="flex items-center gap-2 mt-1.5 mb-2">
        {runningLog && (
          <div className="w-2 h-2 rounded-full bg-running pulse-dot flex-shrink-0" />
        )}
        <div className={`font-mono text-2xl font-bold ${longRunning ? 'text-warn' : runningLog ? 'text-running' : 'text-muted'}`}>
          {runningLog ? formatDuration(elapsed) : '0:00'}
        </div>
        {longRunning && <span className="text-warn text-xs">⚠</span>}
      </div>

      {runningLog && (
        <div className="mt-auto space-y-1.5">
          <div className="flex gap-1.5">
            <button
              onClick={onPause}
              className="flex-1 py-1.5 rounded-md border border-warn/40 bg-warn/10 hover:bg-warn/20 text-warn text-xs font-semibold transition-colors"
            >
              ⏸ On Hold
            </button>
            <button
              onClick={onComplete}
              className="flex-1 py-1.5 rounded-md border border-running/40 bg-running/10 hover:bg-running/20 text-running text-xs font-semibold transition-colors"
            >
              ✓ Complete
            </button>
          </div>
          {hasNext && (
            <button
              onClick={onNext}
              className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border border-accent/40 bg-accent/5 hover:bg-accent/10 transition-colors text-left"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[8px] uppercase tracking-wider text-accent/80 font-semibold leading-none">
                  Next
                </div>
                <div className="text-[10px] font-medium truncate text-text/90 mt-0.5">
                  {runningItem.nextSubTask.name}
                </div>
              </div>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-accent flex-shrink-0">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );

  return createPortal(content, pipWindow.document.body);
}
